/**
 * Root application component.
 *
 * Sets up the application's core providers and routing structure. Wraps the
 * entire application with necessary context providers (authentication, React Query,
 * tooltips) and configures all application routes with authentication protection
 * where required.
 *
 * @module App
 */

import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ConsentGate from "@/components/ConsentGate";
import AdminRoute from "@/components/AdminRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { createQueryClient } from "@/lib/queryClient";
import { allRoutes } from "@/lib/routes";
import { ROUTES } from "@/lib/routeConstants";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminQuestionsPage from "@/pages/admin/AdminQuestionsPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminContextsPage from "@/pages/admin/AdminContextsPage";
import AdminTitlesPage from "@/pages/admin/AdminTitlesPage";
import AdminIntroductionPage from "@/pages/admin/AdminIntroductionPage";
import AdminGenerationPage from "@/pages/admin/AdminGenerationPage";
import AdminSimulationsPage from "@/pages/admin/AdminSimulationsPage";
import AdminAnswersPage from "@/pages/admin/AdminAnswersPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminTokenUsagePage from "@/pages/admin/AdminTokenUsagePage";
import AdminActivationPage from "@/pages/admin/AdminActivationPage";
import AdminPlansPage from "@/pages/admin/AdminPlansPage";
import AdminBackupPage from "@/pages/admin/AdminBackupPage";
import AdminFeedbackPage from "@/pages/admin/AdminFeedbackPage";
import AdminModerationPage from "@/pages/admin/AdminModerationPage";
import AdminHumanReviewsPage from "@/pages/admin/AdminHumanReviewsPage";
import AdminDiagnosticReviewPage from "@/pages/admin/AdminDiagnosticReviewPage";
import { useEffect, useRef } from "react";
import { logger } from "@/lib/logger";
import { useAuth } from "@/hooks/useAuth";
import { navigateToSignedOutSignIn } from "@/lib/authNavigation";
import {
  completeSessionInvalidation,
  subscribeToSessionInvalidation,
} from "@/lib/sessionInvalidation";

// Create QueryClient instance at module level (not recreated on every render)
const queryClient = createQueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

function LoggingObservers() {
  const location = useLocation();
  const previousRouteRef = useRef<string | null>(null);

  useEffect(() => {
    const nextRoute = `${location.pathname}${location.search}`;
    logger.setCurrentRoute(nextRoute);
    logger.event(
      "navigation.route_changed",
      {
        previousRoute: previousRouteRef.current,
        nextRoute,
      },
      "info",
      "Route changed",
    );
    previousRouteRef.current = nextRoute;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      logger.error("Unhandled browser error", event.error ?? event.message, {
        event: "client.error.unhandled",
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.error("Unhandled promise rejection", event.reason, {
        event: "client.promise_rejection.unhandled",
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return null;
}

function SessionInvalidationObserver() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout } = useAuth();

  useEffect(() => {
    return subscribeToSessionInvalidation(async (event) => {
      try {
        logger.event(
          "auth.session.logout_started",
          {
            status: event.status,
            context: event.context,
          },
          "info",
          "Logging out after session invalidation",
        );
        await logout();
        queryClient.clear();
        navigateToSignedOutSignIn(navigate);
      } finally {
        completeSessionInvalidation();
      }
    });
  }, [logout, navigate, queryClient]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <BrowserRouter>
              <ScrollToTop />
              <LoggingObservers />
              <SessionInvalidationObserver />
              <ErrorBoundary>
                <Routes>
                  {allRoutes.map((route) => {
                    const RouteComponent = route.component;
                    let element: React.ReactElement = <RouteComponent />;
                    if (route.requiresAuth) {
                      element = (
                        <ProtectedRoute>
                          {route.requiresConsent ? (
                            <ConsentGate>
                              <RouteComponent />
                            </ConsentGate>
                          ) : (
                            <RouteComponent />
                          )}
                        </ProtectedRoute>
                      );
                    }

                    return (
                      <Route key={route.path} path={route.path} element={element} />
                    );
                  })}
                  <Route
                    path={ROUTES.EXAM_SIMULATION}
                    element={<Navigate to={ROUTES.EXAM} replace />}
                  />
                  {/* Admin routes */}
                  <Route
                    path={ROUTES.ADMIN}
                    element={
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    }
                  >
                    <Route index element={<Navigate to="questions" replace />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="questions" element={<AdminQuestionsPage />} />
                    <Route path="contexts" element={<AdminContextsPage />} />
                    <Route path="titles" element={<AdminTitlesPage />} />
                    <Route path="introduction" element={<AdminIntroductionPage />} />
                    <Route path="generation" element={<AdminGenerationPage />} />
                    <Route path="simulations" element={<AdminSimulationsPage />} />
                    <Route path="answers" element={<AdminAnswersPage />} />
                    <Route path="feedback" element={<AdminFeedbackPage />} />
                    <Route path="moderation" element={<AdminModerationPage />} />
                    <Route path="human-reviews" element={<AdminHumanReviewsPage />} />
                    <Route path="diagnostic-review" element={<AdminDiagnosticReviewPage />} />
                    <Route path="analytics" element={<AdminAnalyticsPage />} />
                    <Route path="token-usage" element={<AdminTokenUsagePage />} />
                    <Route path="activation" element={<AdminActivationPage />} />
                    <Route path="plans" element={<AdminPlansPage />} />
                    <Route path="backup" element={<AdminBackupPage />} />
                  </Route>
                </Routes>
              </ErrorBoundary>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
